<!-- Navbar -->
<nav class="navbar navbar-dark bg-dark border-bottom sticky-top">
    <div class="container">
        <span class="navbar-brand fw-bold fs-4">Binst</span>
        <span>
        <a class="mx-3" href="/">Back to Binst</a>
        <a class="mx-3" href="/privacy">Privacy Policy</a>
        <span class="text-muted small text-uppercase fw-semibold">Terms of Service</span>
        </span>
    </div>
</nav>

<div class="container py-5">
    <div class="row g-5">

        <!-- Sidebar -->
        <div class="col-lg-3 d-none d-lg-block">
            <div class="sticky-top pt-2" style="top: 72px;">
                <p class="text-uppercase text-muted small fw-semibold mb-3">Contents</p>
                <nav class="nav flex-column gap-1">
                    <a class="nav-link text-muted px-0 py-1 small" href="#acceptance">1. Acceptance of Terms</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="#eligibility">2. Eligibility</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="#accounts">3. Your Account</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="#content">4. User Content</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="#prohibited">5. Prohibited Conduct</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="#public">6. Public Nature of the Platform</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="#ip">7. Intellectual Property</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="#dmca">8. Copyright &amp; DMCA</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="#termination">9. Termination</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="#disclaimers">10. Disclaimers</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="#liability">11. Limitation of Liability</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="#indemnity">12. Indemnification</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="#law">13. Governing Law</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="#changes">14. Changes to These Terms</a>
                    <a class="nav-link text-muted px-0 py-1 small" href="#contact">15. Contact Us</a>
                </nav>
            </div>
        </div>

        <!-- Main content -->
        <div class="col-lg-9">

            <!-- Hero -->
            <div class="mb-5 pb-4 border-bottom">
                <h1 class="display-5 fw-bold mb-2">Terms of Service</h1>
                <p class="text-muted mb-3">Effective Date: 6 May 2026</p>
                <div class="alert alert-primary" role="alert">
                    Please read these Terms carefully before using Binst. By accessing or using the platform, you agree to be bound by these Terms. If you do not agree, you must not use Binst.
                </div>
            </div>

            <!-- 1. Acceptance -->
            <section id="acceptance" class="mb-5">
                <h2 class="h4 fw-bold mb-3">1. Acceptance of Terms</h2>
                <p class="text-muted">These Terms of Service ("Terms") constitute a legally binding agreement between you and Binst ("we", "us", or "our") governing your access to and use of the Binst platform, including its website and all related services (collectively, the "Platform").</p>
                <p class="text-muted">By creating an account, logging in, or otherwise using the Platform, you confirm that you have read, understood, and agreed to these Terms, as well as our <a href="binst-privacy-policy.html">Privacy Policy</a>, which is incorporated herein by reference.</p>
            </section>

            <hr class="mb-5" />

            <!-- 2. Eligibility -->
            <section id="eligibility" class="mb-5">
                <h2 class="h4 fw-bold mb-3">2. Eligibility</h2>
                <p class="text-muted">You must meet the following criteria to use Binst:</p>
                <ul class="text-muted">
                    <li>You must be at least <strong>13 years of age</strong>.</li>
                    <li>You must have the legal capacity to enter into a binding agreement in your jurisdiction.</li>
                    <li>You must not be prohibited from using the Platform under any applicable laws.</li>
                    <li>You must not have previously had an account terminated by Binst for violations of these Terms.</li>
                </ul>
                <p class="text-muted">If you are between 13 and 18 years old, you confirm that you have obtained the consent of a parent or legal guardian to use the Platform.</p>
            </section>

            <hr class="mb-5" />

            <!-- 3. Your Account -->
            <section id="accounts" class="mb-5">
                <h2 class="h4 fw-bold mb-3">3. Your Account</h2>

                <h3 class="h6 fw-semibold text-uppercase text-muted mt-4 mb-2">3.1 Google OAuth Login</h3>
                <p class="text-muted">Binst uses Google OAuth to authenticate users. By logging in with your Google account, you authorise Binst to access your Google display name, email address, and profile picture. You are responsible for maintaining the security of your Google account.</p>

                <h3 class="h6 fw-semibold text-uppercase text-muted mt-4 mb-2">3.2 Account Responsibility</h3>
                <p class="text-muted">You are solely responsible for all activity that occurs under your account. You agree to:</p>
                <ul class="text-muted">
                    <li>Keep your login credentials secure and not share access with others.</li>
                    <li>Notify us immediately at <a href="mailto:support@binst.net">support&#64;binst.net</a> if you suspect unauthorised access to your account.</li>
                    <li>Not create accounts on behalf of others without their explicit consent.</li>
                    <li>Not operate more than one account to evade a suspension or ban.</li>
                </ul>

                <h3 class="h6 fw-semibold text-uppercase text-muted mt-4 mb-2">3.3 Account Accuracy</h3>
                <p class="text-muted">You agree that the profile information associated with your account is accurate and not misleading. Impersonating another person, organisation, or entity is strictly prohibited.</p>
            </section>

            <hr class="mb-5" />

            <!-- 4. User Content -->
            <section id="content" class="mb-5">
                <h2 class="h4 fw-bold mb-3">4. User Content</h2>

                <h3 class="h6 fw-semibold text-uppercase text-muted mt-4 mb-2">4.1 Ownership</h3>
                <p class="text-muted">You retain ownership of any text, images, or other content you post on Binst ("User Content"). We do not claim ownership of your content.</p>

                <h3 class="h6 fw-semibold text-uppercase text-muted mt-4 mb-2">4.2 Licence to Binst</h3>
                <p class="text-muted">By posting User Content on Binst, you grant us a worldwide, non-exclusive, royalty-free, sublicensable licence to host, store, display, reproduce, and distribute your content solely for the purpose of operating and providing the Platform. This licence ends when you delete your content or close your account, subject to any copies already distributed publicly.</p>

                <h3 class="h6 fw-semibold text-uppercase text-muted mt-4 mb-2">4.3 Your Representations</h3>
                <p class="text-muted">By posting User Content, you represent and warrant that:</p>
                <ul class="text-muted">
                    <li>You own the content or have the necessary rights and permissions to post it.</li>
                    <li>Your content does not infringe any third-party intellectual property, privacy, or other rights.</li>
                    <li>Your content complies with these Terms and all applicable laws.</li>
                </ul>

                <h3 class="h6 fw-semibold text-uppercase text-muted mt-4 mb-2">4.4 Content Removal</h3>
                <p class="text-muted">We reserve the right, but not the obligation, to remove any User Content that we determine, in our sole discretion, violates these Terms or is otherwise objectionable. We are not liable for any failure or delay in removing such content.</p>
            </section>

            <hr class="mb-5" />

            <!-- 5. Prohibited Conduct -->
            <section id="prohibited" class="mb-5">
                <h2 class="h4 fw-bold mb-3">5. Prohibited Conduct</h2>
                <p class="text-muted">You agree not to use Binst to post, upload, transmit, or otherwise make available any content or engage in any behaviour that:</p>

                <h3 class="h6 fw-semibold text-uppercase text-muted mt-4 mb-2">5.1 Illegal or Harmful Content</h3>
                <ul class="text-muted">
                    <li>Is unlawful, fraudulent, or violates any applicable local, national, or international law</li>
                    <li>Depicts, promotes, or facilitates child sexual abuse or exploitation</li>
                    <li>Incites violence, terrorism, or hatred against any individual or group</li>
                    <li>Promotes self-harm or suicide</li>
                    <li>Contains malware, viruses, or other malicious code</li>
                </ul>

                <h3 class="h6 fw-semibold text-uppercase text-muted mt-4 mb-2">5.2 Harassment &amp; Abuse</h3>
                <ul class="text-muted">
                    <li>Harasses, bullies, threatens, or intimidates any person</li>
                    <li>Publishes another person's private information without their consent (doxxing)</li>
                    <li>Impersonates any person or entity in a misleading or deceptive manner</li>
                </ul>

                <h3 class="h6 fw-semibold text-uppercase text-muted mt-4 mb-2">5.3 Platform Integrity</h3>
                <ul class="text-muted">
                    <li>Uses automated tools, bots, or scrapers to access or interact with the Platform without our prior written consent</li>
                    <li>Attempts to circumvent any security measures or access controls</li>
                    <li>Interferes with or disrupts the integrity or performance of the Platform</li>
                    <li>Creates multiple accounts to evade a ban or suspension</li>
                    <li>Engages in spam, unsolicited advertising, or repetitive posting</li>
                </ul>

                <div class="alert alert-danger mt-4" role="alert">
                    <strong>Zero tolerance:</strong> Any content involving the sexual exploitation of minors will result in immediate account termination and will be reported to the relevant authorities.
                </div>
            </section>

            <hr class="mb-5" />

            <!-- 6. Public Nature -->
            <section id="public" class="mb-5">
                <h2 class="h4 fw-bold mb-3">6. Public Nature of the Platform</h2>
                <div class="alert alert-warning" role="alert">
                    <strong>Binst is a public billboard.</strong> All content posted is visible to everyone — there are no private posts, followers-only settings, or restricted audiences.
                </div>
                <p class="text-muted">You acknowledge and agree that:</p>
                <ul class="text-muted">
                    <li>All content you post is publicly accessible to any person, anywhere in the world, including unregistered visitors.</li>
                    <li>Your content may be indexed by search engines and cached or archived by third parties.</li>
                    <li>You have no reasonable expectation of privacy with respect to any content you post on Binst.</li>
                    <li>Binst is not responsible for how third parties use or distribute your publicly posted content.</li>
                </ul>
                <p class="text-muted">You are strongly advised not to post any personally identifiable, sensitive, financial, or confidential information on Binst.</p>
            </section>

            <hr class="mb-5" />

            <!-- 7. Intellectual Property -->
            <section id="ip" class="mb-5">
                <h2 class="h4 fw-bold mb-3">7. Intellectual Property</h2>
                <p class="text-muted">All rights in the Binst Platform itself — including its design, software, logo, trademarks, and all content created by Binst — are owned by or licensed to us and are protected by applicable intellectual property laws.</p>
                <p class="text-muted">You may not copy, reproduce, modify, distribute, or create derivative works of any Binst-owned content without our prior written permission. Nothing in these Terms grants you any rights in Binst's intellectual property beyond what is expressly stated.</p>
            </section>

            <hr class="mb-5" />

            <!-- 8. DMCA -->
            <section id="dmca" class="mb-5">
                <h2 class="h4 fw-bold mb-3">8. Copyright &amp; DMCA</h2>
                <p class="text-muted">We respect intellectual property rights. If you believe that content posted on Binst infringes your copyright, please send a notice to <a href="mailto:support&#64;binst.net">support&#64;binst.net</a> including:</p>
                <ul class="text-muted">
                    <li>A description of the copyrighted work you claim has been infringed</li>
                    <li>The URL or location of the allegedly infringing content on Binst</li>
                    <li>Your contact information (name, address, email, phone)</li>
                    <li>A statement that you have a good-faith belief the use is not authorised</li>
                    <li>A statement that the information in your notice is accurate, under penalty of perjury</li>
                    <li>Your physical or electronic signature</li>
                </ul>
                <p class="text-muted">We will review valid notices and take appropriate action, which may include removing the infringing content and terminating repeat infringers' accounts.</p>
            </section>

            <hr class="mb-5" />

            <!-- 9. Termination -->
            <section id="termination" class="mb-5">
                <h2 class="h4 fw-bold mb-3">9. Termination</h2>

                <h3 class="h6 fw-semibold text-uppercase text-muted mt-4 mb-2">9.1 By Binst</h3>
                <p class="text-muted">We reserve the right to suspend or permanently terminate your account at any time, with or without notice, if we determine that you have violated these Terms or if your conduct poses a risk to the Platform, other users, or third parties.</p>

                <h3 class="h6 fw-semibold text-uppercase text-muted mt-4 mb-2">9.2 By You</h3>
                <p class="text-muted">You may delete your account at any time through your account settings. Upon deletion, your personal data will be handled as described in our <a href="binst-privacy-policy.html">Privacy Policy</a>. Public content you posted may remain visible unless you delete it before closing your account.</p>

                <h3 class="h6 fw-semibold text-uppercase text-muted mt-4 mb-2">9.3 Effect of Termination</h3>
                <p class="text-muted">Upon termination, your right to access and use the Platform ceases immediately. Provisions of these Terms that by their nature should survive termination — including content ownership, disclaimers, liability limitations, and governing law — shall continue to apply.</p>
            </section>

            <hr class="mb-5" />

            <!-- 10. Disclaimers -->
            <section id="disclaimers" class="mb-5">
                <h2 class="h4 fw-bold mb-3">10. Disclaimers</h2>
                <p class="text-muted">The Platform is provided on an <strong>"as is"</strong> and <strong>"as available"</strong> basis without warranties of any kind, either express or implied. To the fullest extent permitted by law, we disclaim all warranties, including:</p>
                <ul class="text-muted">
                    <li>That the Platform will be uninterrupted, error-free, or secure</li>
                    <li>That any content on the Platform is accurate, complete, or reliable</li>
                    <li>That the Platform will meet your specific requirements</li>
                </ul>
                <p class="text-muted">We are not responsible for the content posted by users. User Content represents the views of the individual poster, not Binst.</p>
            </section>

            <hr class="mb-5" />

            <!-- 11. Limitation of Liability -->
            <section id="liability" class="mb-5">
                <h2 class="h4 fw-bold mb-3">11. Limitation of Liability</h2>
                <p class="text-muted">To the fullest extent permitted by applicable law, Binst and its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of or inability to use the Platform, including but not limited to:</p>
                <ul class="text-muted">
                    <li>Loss of data or content</li>
                    <li>Loss of revenue or profits</li>
                    <li>Damage to reputation</li>
                    <li>Any content posted by other users</li>
                </ul>
                <p class="text-muted">Our total liability to you for any claim arising from these Terms or your use of the Platform shall not exceed the greater of <strong>ZAR 500</strong> or the amount you paid us (if any) in the 12 months preceding the claim.</p>
            </section>

            <hr class="mb-5" />

            <!-- 12. Indemnification -->
            <section id="indemnity" class="mb-5">
                <h2 class="h4 fw-bold mb-3">12. Indemnification</h2>
                <p class="text-muted">You agree to indemnify, defend, and hold harmless Binst and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses (including reasonable legal fees) arising out of or in connection with:</p>
                <ul class="text-muted">
                    <li>Your use of the Platform</li>
                    <li>Your User Content</li>
                    <li>Your violation of these Terms</li>
                    <li>Your violation of any third-party rights, including intellectual property or privacy rights</li>
                </ul>
            </section>

            <hr class="mb-5" />

            <!-- 13. Governing Law -->
            <section id="law" class="mb-5">
                <h2 class="h4 fw-bold mb-3">13. Governing Law</h2>
                <p class="text-muted">These Terms are governed by and construed in accordance with the laws of the <strong>Republic of South Africa</strong>, without regard to its conflict of law principles.</p>
                <p class="text-muted">Any disputes arising under these Terms shall be subject to the exclusive jurisdiction of the courts of South Africa. If you are accessing the Platform from outside South Africa, you do so at your own risk and are responsible for compliance with local laws.</p>
            </section>

            <hr class="mb-5" />

            <!-- 14. Changes -->
            <section id="changes" class="mb-5">
                <h2 class="h4 fw-bold mb-3">14. Changes to These Terms</h2>
                <p class="text-muted">We may update these Terms from time to time. When we do, we will update the effective date at the top of this page. For significant changes, we will notify registered users via email or a prominent notice on the Platform.</p>
                <p class="text-muted">Your continued use of Binst after any changes take effect constitutes your acceptance of the revised Terms. If you do not agree to the updated Terms, you must stop using the Platform and may delete your account.</p>
            </section>

            <hr class="mb-5" />

            <!-- 15. Contact -->
            <section id="contact" class="mb-5">
                <h2 class="h4 fw-bold mb-3">15. Contact Us</h2>
                <p class="text-muted">If you have any questions about these Terms or wish to report a violation, please contact us:</p>
                <div class="card border mt-3">
                    <div class="card-body">
                        <h5 class="card-title h6 fw-semibold mb-3">Binst Legal Team</h5>
                        <p class="mb-1 text-muted small"><strong>General:</strong> <a href="mailto:support@binst.net">support&#64;binst.net</a></p>
                        <p class="mb-1 text-muted small"><strong>Legal &amp; Copyright:</strong> <a href="mailto:support@binst.net">support&#64;binst.net</a></p>
                        <p class="mb-0 text-muted small"><strong>Website:</strong> <a href="https://www.binst.com" target="_blank">binst.sewell.digital</a></p>
                    </div>
                </div>
            </section>

        </div>
    </div>
</div>

<!-- Footer -->
<footer class="border-top bg-dark mt-5 py-4">
    <div class="container d-flex flex-column flex-md-row justify-content-between align-items-center gap-2">
        <span class="fw-bold">Binst</span>
        <span class="text-muted small">&copy; 2026 Binst. All rights reserved.</span>
        <span class="text-muted small">Effective: 6 May 2026</span>
    </div>
</footer>
